import React, { useState, useEffect, useRef } from 'react';
import { MoreHorizontal } from 'lucide-react';

/**
 * SafetyMenu — small overflow ⋯ button that opens a popover with a list
 * of actions. Each action is a `{ key, label, onClick, danger?, testid }` item.
 *
 * Phase 5 polish:
 *   - ARIA menu pattern: `aria-haspopup="menu"`, `aria-expanded`, `role="menu"`,
 *     and `role="menuitem"` on each entry so screen readers announce the
 *     popover as a real menu.
 *   - Escape closes the popover and returns focus to the trigger.
 *   - 32px minimum tap target on the trigger (was 24px on `size=sm`).
 */
const SafetyMenu = ({ items, t, isRTL, testid = 'safety-menu', size = 'sm', align = 'end' }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items || items.length === 0) return null;

  // Phase 5 — keep the visual size variants but lift the actual hit-area to
  // ~32px (sm) / ~40px (md) so touch targets pass Apple/Material guidelines.
  const btnSize = size === 'sm' ? 'p-2 min-w-[32px] min-h-[32px]' : 'p-2.5 min-w-[40px] min-h-[40px]';
  const alignClass = align === 'start' ? (isRTL ? 'right-0' : 'left-0') : isRTL ? 'left-0' : 'right-0';

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testid}-wrap`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        data-testid={testid}
        aria-label={t('moreActions')}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${btnSize} inline-flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100`}
      >
        <MoreHorizontal aria-hidden="true" className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t('moreActions')}
          className={`absolute top-full mt-1 ${alignClass} w-52 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-40`}
          data-testid={`${testid}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.onClick && it.onClick();
              }}
              data-testid={it.testid || `${testid}-${it.key}`}
              className={`w-full text-start px-3 py-2.5 text-sm hover:bg-gray-50 ${
                it.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SafetyMenu;
